import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  useToast,
  Text,
  FormHelperText,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import api from '../../services/api';

/**
 * Modal for creating a new task
 * Shown immediately after task completion when user selects "Create another task"
 */
const NewTaskModal = ({ 
  isOpen, 
  onClose, 
  leadId,
  leadDetails,
  onTaskCreated
}) => {
  const [formData, setFormData] = useState({
    title: '',
    priority: 'medium',
    dueDate: '',
    assignedTo: '',
    category: 'Follow-up'
  });
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const toast = useToast();

  // Fetch users for assignment dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isOpen) return;
      
      setIsFetching(true);
      try {
        const response = await api.get('/api/users');
        if (response.data?.users) {
          setUsers(response.data.users);
        }
      } catch (error) {
        console.error('[NewTaskModal] Error fetching users:', error);
        toast({
          title: 'Error fetching users',
          description: 'Could not load user list',
          status: 'error',
          duration: 3000
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchUsers();
  }, [isOpen, toast]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle assignee selection
  const handleAssigneeChange = (e) => {
    const userId = e.target.value;
    const user = users.find(u => u.uid === userId) || { uid: '', email: '' };
    
    setFormData(prev => ({
      ...prev,
      assignedTo: {
        uid: user.uid,
        email: user.email || ''
      }
    }));
  };

  // Get ISO string from date for datetime-local input
  const getLocalDateTimeString = (date) => {
    const d = date ? new Date(date) : new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  // Helper function to set due date presets
  const setDuePreset = (preset) => {
    const now = new Date();
    let dueDate = new Date(now);
    
    switch(preset) {
      case 'today':
        // End of business today (6 PM)
        dueDate.setHours(18, 0, 0, 0);
        break;
      case 'tomorrow':
        dueDate.setDate(now.getDate() + 1);
        dueDate.setHours(12, 0, 0, 0);
        break;
      case 'nextWeek':
        dueDate.setDate(now.getDate() + 7);
        dueDate.setHours(12, 0, 0, 0);
        break;
      case '2hours':
        dueDate.setHours(now.getHours() + 2);
        break;
      case '6hours':
        dueDate.setHours(now.getHours() + 6);
        break;
      case '3days':
        dueDate.setDate(now.getDate() + 3);
        dueDate.setHours(12, 0, 0, 0);
        break;
      default:
        break;
    }
    
    setFormData(prev => ({
      ...prev,
      dueDate: getLocalDateTimeString(dueDate)
    }));
  };

  // Submit the form
  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast({
        title: 'Title required',
        description: 'Please enter a task title',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    setIsLoading(true);
    try {
      // If we have a due date, ensure it's in ISO string format for the API
      let dueDate = formData.dueDate;
      if (dueDate) {
        const dueDateObj = new Date(dueDate);
        dueDate = dueDateObj.toISOString();
      }
      
      const taskPayload = {
        ...formData,
        dueDate,
        leadId,
        status: 'pending'
      };
      
      const response = await api.post('/api/tasks', taskPayload);
      
      if (response.status === 201) {
        toast({
          title: 'Task created',
          description: 'New task created successfully',
          status: 'success',
          duration: 3000
        });
        
        if (onTaskCreated) {
          onTaskCreated(response.data.task);
        }
        
        onClose();
      }
    } catch (error) {
      console.error('[NewTaskModal] Error creating task:', error);
      toast({
        title: 'Error creating task',
        description: error.response?.data?.message || 'Could not create task',
        status: 'error',
        duration: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Follow-up Task</ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {leadDetails && (
              <Box bg="blue.50" p={2} borderRadius="md" mb={2}>
                <Text fontWeight="medium">
                  Lead: {leadDetails.brideAndGroomName || 
                        (leadDetails.firstName && leadDetails.lastName ? 
                        `${leadDetails.firstName} ${leadDetails.lastName}` : 
                        (leadDetails.email || 'Unknown'))}
                </Text>
                {leadDetails.email && (
                  <Text fontSize="sm" color="gray.600">{leadDetails.email}</Text>
                )}
              </Box>
            )}
            
            <FormControl isRequired>
              <FormLabel>Task Title</FormLabel>
              <Input 
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter task title"
              />
            </FormControl>
            
            <FormControl>
              <FormLabel>Priority</FormLabel>
              <Select 
                name="priority"
                value={formData.priority}
                onChange={handleChange}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </Select>
            </FormControl>
            
            <FormControl>
              <FormLabel>Due Date and Time</FormLabel>
              <HStack spacing={2}>
                <Input 
                  type="datetime-local"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  flex="1"
                />
                <Menu>
                  <MenuButton 
                    as={Button} 
                    size="sm" 
                    rightIcon={<ChevronDownIcon />}
                    colorScheme="blue"
                    variant="outline"
                  >
                    Presets
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => setDuePreset('2hours')}>In 2 hours</MenuItem>
                    <MenuItem onClick={() => setDuePreset('6hours')}>In 6 hours</MenuItem>
                    <MenuItem onClick={() => setDuePreset('today')}>End of today</MenuItem>
                    <MenuItem onClick={() => setDuePreset('tomorrow')}>Tomorrow</MenuItem>
                    <MenuItem onClick={() => setDuePreset('3days')}>In 3 days</MenuItem>
                    <MenuItem onClick={() => setDuePreset('nextWeek')}>Next week</MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </FormControl>
            
            <FormControl>
              <FormLabel>Assigned To</FormLabel>
              <Select
                value={formData.assignedTo?.uid || ''}
                onChange={handleAssigneeChange}
                isDisabled={isFetching}
              >
                <option value="">Unassigned</option>
                {users.map(user => (
                  <option key={user.uid} value={user.uid}>
                    {user.displayName || user.email}
                  </option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="outline" onClick={onClose} isDisabled={isLoading}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSubmit}
              isLoading={isLoading}
              loadingText="Creating..."
            >
              Create Task
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewTaskModal;
